import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { ButtonV1, ModalBox } from "components";
import DocumentUploader from "./components/DocumentUploader";

import { postCall_v2 } from "services";
import { decimalRoundOff, getAttributes, moneyFormat, validateErrorMsg } from "utils";
import { useToaster } from "hooks";
import { error, success } from "constants";
import { ColumnTitle } from "CustomStyledComponents";
import { DISPATCH_ORDER } from "./constants";

const BulkRequestShortCloseModal = ({ open, toggleModal, data = {} }) => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const [uploadedFiles, setUploadedFiles] = useState();
  const {record, checkedLineItems = [], orderNumber} = data;

  const { control, getValues, setValue } = useForm({
    mode: "onTouched",
  });

  const onFileUpload = async (file) => {
    try {
      const formData = new FormData();
  
      formData.append("file", file[file.length - 1]);  
      formData.append("psId", record?.psIdentifier);
      formData.append("orderNumber", orderNumber);
      checkedLineItems.forEach((item) => {
        formData.append("lineItemIdList", item?.lineItemId);
      });
  
      const res = await postCall_v2(
        "/oms/plant-supply/do/short-close-bulk/upload",
        formData
      );
  
      const uploadedFile = res?.data?.[0]?.doShortClosureFile?.sellerConfirmationFiles
      setUploadedFiles(uploadedFile);
    } catch (err) {
      triggerToaster(validateErrorMsg(err?.data?.title), error);
    }
  };

  const onRequestShortClose = async () => {
    if (!uploadedFiles) {
      triggerToaster(`Please attach seller confirmation file`, error);
      return;
    }
  
    const remark = getValues("remarks");
  
    const payload = {
      bulkDoShortClosureRequest: checkedLineItems.map((item) => ({
        psIdentifier: record?.psIdentifier,
        orderNumber,
        lineItemIds: [item?.lineItemId],
        remarks: remark || "",
      })),
    };
  
    try {
      const res = await postCall_v2(
        `/joms/api/dispatch-order/external/v1/bulk-short-close-do`,
        payload
      );

      if(res?.data?.success) {
        triggerToaster("Do Short closure request initiated successfully", success);
      } else {
        triggerToaster(validateErrorMsg(res?.data?.message), error);
      } 
    } catch (err) {
      triggerToaster(validateErrorMsg(err?.data?.detail), error);
    } finally {
      setValue("remarks", "");
      toggleModal();
      setUploadedFiles();
    }
  };
  
  useEffect(() => {
    setUploadedFiles();
    setValue("remarks", "");
  }, [open]);

  return (
    <>
      <ModalBox open={open} onCloseModal={toggleModal} width="80%">
        <Box style={{ padding: 20 }}>
          <Box display={"flex"} mb={4}>
            <Typography fontSize={20} fontWeight={600}>
              Request for Bulk DO short closure
            </Typography>
          </Box>

          <Divider style={{ marginBottom: 15 }} />
          
          <TableContainer component={Paper} style={{ marginBottom: 30 }}>
            <Table sx={{
                "& .MuiTableCell-head": {
                    backgroundColor: "#eee !important",
                    fontWeight: "bold",
            }}}>
                <TableHead>
                <TableRow> 
                    {DISPATCH_ORDER.bulkShortClosureModalHeader.map((heading, index) => {
                      return (
                        <TableCell key={index}>
                          <ColumnTitle>{heading}</ColumnTitle>
                        </TableCell>
                      );
                    })}
                </TableRow>
                </TableHead>

                <TableBody>
                  {checkedLineItems.map(item => (
                    <TableRow key={item?.lineItemId}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {item?.productInfo?.name}
                        {getAttributes(item?.productInfo?.attributes)}</TableCell>
                      <TableCell>
                        {item?.releaseQuantity
                            ? `${item?.releaseQuantity} ${item?.measurementUnit}`
                            : "-"
                        }
                      </TableCell>
                      <TableCell>
                        {item?.shipmentLinkList?.[0]?.shippedQuantity
                            ? `${item?.shipmentLinkList?.[0]?.shippedQuantity} ${item?.measurementUnit}`
                            : "-"
                        }
                      </TableCell>
                      <TableCell>
                        {decimalRoundOff(
                            (item?.releaseQuantity ?? 0) -
                            (item?.shipmentLinkList?.[0]?.shippedQuantity ?? 0),
                        )} {item?.measurementUnit}
                      </TableCell>
                      <TableCell>
                        {moneyFormat(
                            ((item?.releaseQuantity ?? 0) -
                            (item?.shipmentLinkList?.[0]?.shippedQuantity ?? 0)) *
                            item?.price,
                        )}
                      </TableCell>
                      <TableCell>
                        {moneyFormat(item?.releaseQuantity * item?.price)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </Table>
          </TableContainer>

          <Grid container marginBottom={15}>

            <Grid item xs={12} sm={4}>
              <Stack spacing={2}>
                <Typography>
                  Attach seller confirmation{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>

                <Box width="95%">
                  <DocumentUploader
                    onUpload={onFileUpload}
                    uploadedFiles={uploadedFiles}
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Stack direction="column" spacing={2}>
                <Typography>
                  Enter comment
                </Typography>

                <Controller
                    control={control}
                    name={"remarks"}
                    render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Comment here"
                      multiline
                      maxRows={4}
                      size="small"
                      style={{
                        outline: "none",
                        borderRadius: 8,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        marginTop: "11px",
                      }} 
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "transparent",   // default border
                          },
                          "&:hover fieldset": {
                            borderColor: "transparent",   // on hover
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "transparent",   // on focus
                          }
                        }
                      }}     
                    />
                    )}
                />
              </Stack>
            </Grid>
          </Grid>

          <Box display={"flex"} justifyContent={"flex-end"} gap={3}>
            <ButtonV1
                title={"Cancel"}
                variant="outlined"
                onClick={toggleModal}
            />
            <ButtonV1 title={"Request"} onClick={onRequestShortClose} />
          </Box>
        </Box>
      </ModalBox>
    </>
  );
};

export default BulkRequestShortCloseModal;
